import { evaluateConditions } from '../../../core/assistant/utils/conditionEvaluation';
import type { AtlasSavedViewDefinition } from '../models/AtlasSavedView';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import { normalizeFieldRecord } from '../assistant/orgAssistantFieldDiscovery';
import { buildPeopleChartScene } from './buildPeopleChartScene';
import { computeOrgReportingManagerReferences } from './orgChartReportCounts';

/**
 * Evaluates a saved view's enabled filter rules against an already
 * permissioned org model, returning the matching person IDs (open seats
 * excluded — a vacant position can never itself match a rule).
 *
 * Isomorphic and side-effect free so the interactive client (`usePeopleChartSync`)
 * and the non-interactive export pipeline can share one evaluator and can't
 * drift into rendering different memberships for the same definition.
 */
export function resolveAtlasSavedViewMatches(
  model: OrgModelProjection,
  definition: AtlasSavedViewDefinition,
): string[] {
  const rules = definition.filterRules.filter((rule) => rule.enabled);
  const fullScene = buildPeopleChartScene(model);
  const reportingManagerReferences = computeOrgReportingManagerReferences(
    fullScene.nodes,
    fullScene.edges,
  );
  // The people-card projection deliberately uses the opaque source record key
  // as `personId`. Saved `reportsTo` conditions, however, are selected from
  // the source Person ID field (for example, `EMP0004`). Resolve that value
  // while evaluating membership instead of adding the source ID to the scene
  // returned through a public link.
  const savedFilterPersonIds = new Map<string, string>();
  for (const person of model.people) {
    const props = person.props as Record<string, unknown>;
    const sourcePersonId = [props.employeeId, props.personId, person.externalId, person.id]
      .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (!sourcePersonId) continue;
    savedFilterPersonIds.set(person.id, sourcePersonId);
    if (person.externalId) savedFilterPersonIds.set(person.externalId, sourcePersonId);
  }
  return fullScene.nodes
    .filter((node) => !node.id.startsWith('open-seat:') && rules.every((rule) => (
      evaluateConditions(
        {
          ...node,
          attrs: {
            ...(node.attrs ?? {}),
            ...normalizeFieldRecord((node.attrs ?? {}) as Record<string, unknown>),
            ...(reportingManagerReferences.has(node.id)
              ? {
                reportsTo: savedFilterPersonIds.get(
                  reportingManagerReferences.get(node.id)?.personId ?? '',
                ) ?? reportingManagerReferences.get(node.id)?.personId,
              }
              : {}),
          },
          props: {
            ...(node.props ?? {}),
            ...normalizeFieldRecord((node.props ?? {}) as Record<string, unknown>),
          },
        },
        rule.conditions,
        rule.logic,
      )
    )))
    .map((node) => node.id);
}
